import React, { useEffect, useState } from "react"
import HyperDiv from "../../../../ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import SubH2 from "../../Global/atoms/Text/SubH2"
import FormFieldLabel from "../atoms/FormFieldLabel"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import InfoBubble from "../../Global/atoms/InfoBubble"
import { TOnChange } from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"
import MultilineInput from "components/ProjectNonSpecificComponents/Elements/MultilineInput"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import PlaceholderText from "../../Global/atoms/PlaceholderText"

interface Props {
	direction: TFormFieldDirection
	label: string
	tooltip?: string
	placeholderValue?: string | number | undefined
	name?: string
	value?: string
	editable?: boolean
	multiline?: boolean
	onChange?: TOnChange<HTMLInputElement> & TOnChange<HTMLTextAreaElement>
	centerSpaceMultiplier?: number
	tooltipBelowInputField?: boolean
	containerStyle?: React.CSSProperties
	inputRef?: React.Ref<HTMLInputElement> & React.Ref<HTMLTextAreaElement>
	readOnly?: boolean
	hideLabel?: boolean
	children?: React.ReactNode
}

const FormField = ({
	label,
	tooltip,
	centerSpaceMultiplier = 1,
	tooltipBelowInputField,
	containerStyle,
	children,
	placeholderValue = "",
	name,
	value,
	editable,
	multiline,
	onChange,
	inputRef,
	readOnly = false,
	hideLabel = false,
	direction,
}: Props) => {
	const { isMobile } = useScreenType()
	const labelSpaceProportion = isMobile ? 3 : tooltipBelowInputField ? 1 : 1.5
	const inputSpaceProportion = isMobile
		? 4
		: tooltipBelowInputField
		? 6.5 * centerSpaceMultiplier
		: 5 * centerSpaceMultiplier
	const tooltipSpaceProportion = 3.5
	// Exists for the purpose of clearing default value and setting the new one,
	// when a new one is passed
	const [doResetInput, setDoResetInput] = useState<boolean>(false)

	useEffect(() => {
		if (!editable) return
		if (doResetInput) setDoResetInput(false)
	}, [doResetInput])

	useEffect(() => {
		if (!editable) return
		setDoResetInput(true)
	}, [placeholderValue])

	return (
		<HyperDiv
			direction="column"
			id="form-field-container"
			height="100%"
			style={{
				padding: direction === "Horizontal" ? "8px 16px" : undefined,
				...containerStyle,
			}}
		>
			<HyperDiv
				id="form-field-main-content"
				direction={direction == "Horizontal" ? "row" : "column"}
				height="100%"
			>
				{!hideLabel && (
					<>
						<HyperDiv flex={labelSpaceProportion} centeredY>
							<FormFieldLabel children={label} />
							{!isMobile && direction === "Vertical" && !tooltipBelowInputField && (
								<>
									<Whitespace h x="1" />
									<HyperDiv>{tooltip && <InfoBubble tooltipText={tooltip} />}</HyperDiv>
								</>
							)}
						</HyperDiv>
						{direction == "Horizontal" ? <Whitespace h x=".5" /> : <Whitespace x="0.5" />}
					</>
				)}
				<HyperDiv hidden={editable}>
					<HyperDiv padding=".375em 0 0 0" centered>
						<PlaceholderText children={placeholderValue} />
					</HyperDiv>
				</HyperDiv>
				<HyperDiv
					id="form-field-input-container"
					flex={inputSpaceProportion}
					className="justify-end"
					direction="column"
				>
					{children
						? children
						: !doResetInput &&
						  (multiline ? (
								<MultilineInput
									style={{ width: "100%" }}
									name={name}
									value={value}
									defaultValue={placeholderValue.toString()}
									readOnly={readOnly}
									onChange={onChange}
									ref={inputRef}
								/>
						  ) : (
								<Input
									style={{ width: "100%" }}
									name={name}
									value={value}
									defaultValue={placeholderValue.toString()}
									readOnly={readOnly}
									onChange={onChange}
									ref={inputRef}
								/>
						  ))}
				</HyperDiv>

				{!isMobile && direction === "Horizontal" && !tooltipBelowInputField && (
					<HyperDiv flex={tooltipSpaceProportion}>
						{tooltip && <InfoBubble tooltipText={tooltip} />}
					</HyperDiv>
				)}
			</HyperDiv>

			{tooltipBelowInputField && (
				<>
					<Whitespace x=".5" />
					<HyperDiv direction="row">
						<HyperDiv flex={labelSpaceProportion}>{/* Empty Space */}</HyperDiv>
						{tooltip && (
							<>
								<Whitespace h x=".5" />
								<HyperDiv flex={inputSpaceProportion}>
									<SubH2>{tooltip}</SubH2>
								</HyperDiv>
							</>
						)}
					</HyperDiv>
				</>
			)}
		</HyperDiv>
	)
}

export default FormField

export type TFormFieldDirection = "Horizontal" | "Vertical"
